import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/Avatar";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/AlertDialog";
import { Plus, Search, Edit, Trash2, ExternalLink, Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import { influencerService, type Influencer } from "../services/influencers";
import { toast } from "sonner";

export function InfluencersPage() {
  const navigate = useNavigate();
  const [influencers, setInfluencers] = useState<Influencer[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [activeTotal, setActiveTotal] = useState(0);
  const [deleting, setDeleting] = useState<number | null>(null);
  const [toggling, setToggling] = useState<number | null>(null);

  useEffect(() => {
    fetchInfluencers();
  }, [currentPage]);

  const fetchInfluencers = async () => {
    try {
      setLoading(true);
      const response = await influencerService.getInfluencers(currentPage, {
        search: search || undefined,
        per_page: 15,
      });
      setInfluencers(response.data || []);
      setLastPage(response.meta?.last_page || 1);
      setTotal(response.meta?.total || 0);
      setActiveTotal(response.meta?.active_total || 0);
    } catch {
      toast.error("Erro ao carregar influencers");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchInfluencers();
  };

  const handleToggle = async (influencer: Influencer) => {
    if (!influencer.id) return;
    try {
      setToggling(influencer.id);
      await influencerService.toggleInfluencer(influencer.id);
      const wasActive = isActive(influencer);
      toast.success(wasActive ? "Influencer desativado" : "Influencer ativado");
      fetchInfluencers();
    } catch {
      toast.error("Erro ao atualizar status");
    } finally {
      setToggling(null);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      setDeleting(id);
      await influencerService.deleteInfluencer(id);
      toast.success("Influencer excluído com sucesso");
      fetchInfluencers();
    } catch {
      toast.error("Erro ao excluir influencer");
    } finally {
      setDeleting(null);
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const isActive = (influencer: Influencer) => {
    return influencer.active === true || Number(influencer.active) === 1;
  };

  const totalInfluencers = total;
  const activeInfluencers = activeTotal;

  if (loading && influencers.length === 0) {
    return (
      <main className="flex-1 p-6 bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#D4C070]" />
      </main>
    );
  }

  return (
    <main className="flex-1 p-6 bg-gray-50">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[#68686A]">Influencers</h1>
        <p className="text-[#68686A]">Gerencie parcerias com influenciadores digitais</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <Card className="border border-gray-200 hover:shadow-md transition-shadow duration-200">
          <CardHeader>
            <CardTitle className="text-[#68686A]">Total de Influencers</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold text-gray-900">{totalInfluencers}</p>
            <p className="text-sm text-[#68686A]">Parceiros cadastrados</p>
          </CardContent>
        </Card>

        <Card className="border border-gray-200 hover:shadow-md transition-shadow duration-200">
          <CardHeader>
            <CardTitle className="text-[#68686A]">Ativos</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold text-gray-900">{activeInfluencers}</p>
            <p className="text-sm text-[#849D70]">Campanhas ativas</p>
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Buscar influencers..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            className="pl-10 border-gray-300 focus:ring-2 focus:ring-[#D4C070] focus:border-transparent"
          />
        </div>
        <Button
          variant="outline"
          onClick={handleSearch}
          className="border-gray-300 text-[#68686A] hover:bg-[#EEF1F2] hover:text-[#D4C070]"
        >
          <Search className="h-4 w-4 mr-2" />
          Buscar
        </Button>
        <Button
          onClick={() => navigate("/novo-influencer")}
          className="bg-[#D4C070] hover:bg-[#849D70] text-white"
        >
          <Plus className="h-4 w-4 mr-2" />
          Novo Influencer
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 mb-6">
        {influencers.map((influencer) => (
          <Card key={influencer.id} className="border border-gray-200 hover:shadow-md transition-shadow duration-200">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <Avatar className="w-12 h-12">
                    <AvatarImage src={influencer.cover || undefined} />
                    <AvatarFallback className="bg-[#D4C070] text-white">
                      {getInitials(influencer.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <CardTitle className="text-lg text-gray-900">{influencer.name}</CardTitle>
                    <p className="text-sm text-[#68686A]">/{influencer.slug}</p>
                  </div>
                </div>
                <button
                  onClick={() => handleToggle(influencer)}
                  disabled={toggling === influencer.id}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#D4C070] focus:ring-offset-2 disabled:opacity-50 ${isActive(influencer) ? 'bg-[#849D70]' : 'bg-gray-300'
                    }`}
                  title={isActive(influencer) ? 'Clique para desativar' : 'Clique para ativar'}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isActive(influencer) ? 'translate-x-6' : 'translate-x-1'
                      }`}
                  />
                </button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {influencer.description && (
                  <p className="text-sm text-[#68686A] line-clamp-2">{influencer.description}</p>
                )}

                <div className="flex items-center gap-1 border-t pt-3 mt-3 text-sm">
                  <span className="text-[#68686A]">Produtos:</span>
                  <span className="font-medium text-gray-900">{influencer.products_count || 0}</span>
                </div>

                <div className="flex gap-2 pt-3">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => navigate(`/editar-influencer/${influencer.id}`)}
                    className="flex-1 text-[#68686A] hover:bg-[#EEF1F2] hover:text-[#D4C070] border-gray-300"
                  >
                    <Edit className="h-3 w-3 mr-2" />
                    Editar
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => window.open(`${import.meta.env.VITE_STORE_URL}/influencers/${influencer.slug}`, "_blank")}
                    className="text-[#68686A] hover:bg-[#EEF1F2] hover:text-[#D4C070] border-gray-300"
                    title="Visualizar página do influencer"
                  >
                    <ExternalLink className="h-3 w-3" />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 hover:bg-red-50 hover:text-red-700 border-gray-300"
                        title="Excluir influencer"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Excluir Influencer?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Esta ação não pode ser desfeita. Todos os dados do influencer serão removidos permanentemente.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => handleDelete(influencer.id!)}
                          disabled={deleting === influencer.id}
                          className="bg-red-500 hover:bg-red-600"
                        >
                          {deleting === influencer.id ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Excluindo...
                            </>
                          ) : (
                            "Excluir"
                          )}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {influencers.length === 0 && !loading && (
        <Card className="border border-gray-200">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <p className="text-[#68686A] text-lg mb-4">Nenhum influencer encontrado</p>
            <Button
              onClick={() => navigate("/novo-influencer")}
              className="bg-[#D4C070] hover:bg-[#849D70] text-white"
            >
              <Plus className="h-4 w-4 mr-2" />
              Cadastrar primeiro influencer
            </Button>
          </CardContent>
        </Card>
      )}

      {lastPage > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-[#68686A]">
            Mostrando {influencers.length} de {total} resultados
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="border-gray-300"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            {Array.from({ length: Math.min(5, lastPage) }, (_, i) => {
              const page = Math.max(1, Math.min(currentPage - 2, lastPage - 4)) + i;
              if (page > lastPage) return null;
              return (
                <Button
                  key={page}
                  variant={currentPage === page ? "default" : "outline"}
                  size="sm"
                  onClick={() => setCurrentPage(page)}
                  className={
                    currentPage === page
                      ? "bg-[#D4C070] hover:bg-[#849D70] text-white border-[#D4C070]"
                      : "border-gray-300"
                  }
                >
                  {page}
                </Button>
              );
            })}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((p) => Math.min(lastPage, p + 1))}
              disabled={currentPage === lastPage}
              className="border-gray-300"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </main>
  );
}

export default InfluencersPage;
